import type { DotProps } from "@/v2-backoffice-client/components/dot"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import { CUSTOMER_MERGE_RULE } from "@/v2-backoffice-client/customer/merge/customer.merge.utils"

// How a customer relates to its HubSpot company link (acc.CustomerCompanies),
// derived client-side from the link row — the customer sibling of
// ContactSyncStatus. Under the D0 app-wins contract there is no
// "mismatch" state: once the app has an opinion on a customer's identity it
// overwrites HubSpot on the next push, so divergence is only ever "not pushed
// yet". A customer the app has no opinion on mirrors its company instead.
// MERGED_AWAY is the one state that is about the company rather than the
// link: HubSpot merged it into another company, and this row is what the
// merge left behind (Customer.MergedInto).
export const COMPANY_LINK_STATUS_MERGED_AWAY = "MERGED_AWAY"
export const COMPANY_LINK_STATUS_NOT_LINKED = "NOT_LINKED"
export const COMPANY_LINK_STATUS_PENDING_PUSH = "PENDING_PUSH"
export const COMPANY_LINK_STATUS_IN_SYNC = "IN_SYNC"

export type CompanyLinkStatusValue =
  | typeof COMPANY_LINK_STATUS_MERGED_AWAY
  | typeof COMPANY_LINK_STATUS_NOT_LINKED
  | typeof COMPANY_LINK_STATUS_PENDING_PUSH
  | typeof COMPANY_LINK_STATUS_IN_SYNC

// One label + dot color per status — the single source for the badge, table
// grouping/export values, and the healthy display.
const STATUS_DISPLAY: Record<
  CompanyLinkStatusValue,
  { label: string; variant: DotProps["variant"] }
> = {
  [COMPANY_LINK_STATUS_MERGED_AWAY]: {
    label: "Merged away",
    variant: "destructive",
  },
  [COMPANY_LINK_STATUS_NOT_LINKED]: {
    label: "Not linked",
    variant: "warning",
  },
  [COMPANY_LINK_STATUS_PENDING_PUSH]: {
    label: "Pending push",
    variant: "orange",
  },
  [COMPANY_LINK_STATUS_IN_SYNC]: { label: "In sync", variant: "success-1" },
}

// deriveCompanyLinkStatus computes the badge state from the customer row:
// a company HubSpot merged away = merged away (whatever the link row says:
// a leftover is usually unlinked, and a dead link the publish backfill
// re-created is dropped by the next sync walk); no link row = not linked;
// an app-asserted identity whose last outbound push predates the
// customer's last edit (or never happened) = pending push; otherwise the
// two sides agree.
//
// ChangeSourceCode is what separates the two "never pushed" cases. A customer
// nobody has edited in the app has nothing to push — its identity is mirrored
// FROM the company by the inbound sync, so it is in sync by construction.
// Without this check every synced customer would sit on an orange "Pending
// push" that no run will ever clear.
export function deriveCompanyLinkStatus(
  customer: Pick<
    CustomerFragment,
    "Company" | "UpdatedAt" | "ChangeSourceCode" | "MergedInto"
  >,
): CompanyLinkStatusValue {
  // A leftover an operator kept as a separate customer is an ordinary
  // customer without a CRM company: not linked, not a problem.
  if (customer.MergedInto && !customer.MergedInto.IsKeptSeparate)
    return COMPANY_LINK_STATUS_MERGED_AWAY
  if (!customer.Company) return COMPANY_LINK_STATUS_NOT_LINKED
  if (customer.ChangeSourceCode == null) return COMPANY_LINK_STATUS_IN_SYNC

  const pushedAt = customer.Company.LastPushedAt
    ? Date.parse(customer.Company.LastPushedAt)
    : null
  const updatedAt = customer.UpdatedAt ? Date.parse(customer.UpdatedAt) : null

  if (pushedAt === null) return COMPANY_LINK_STATUS_PENDING_PUSH
  if (updatedAt !== null && updatedAt > pushedAt) {
    return COMPANY_LINK_STATUS_PENDING_PUSH
  }
  return COMPANY_LINK_STATUS_IN_SYNC
}

// Grouping / export / client-side-filter value for a status.
export function getCompanyLinkStatusLabel(
  status: CompanyLinkStatusValue,
): string {
  return STATUS_DISPLAY[status].label
}

export type CompanyLinkStatusConfig = {
  label: string
  variant: DotProps["variant"]
  description: string
  // The action an operator should reach for.
  fix: string
}

// Only the actionable states carry a config; IN_SYNC is handled separately
// so the table stays clean (mirrors userContact.constants).
const ACTIONABLE_CONFIG: Partial<
  Record<CompanyLinkStatusValue, CompanyLinkStatusConfig>
> = {
  [COMPANY_LINK_STATUS_MERGED_AWAY]: {
    ...STATUS_DISPLAY[COMPANY_LINK_STATUS_MERGED_AWAY],
    description: CUSTOMER_MERGE_RULE,
    fix: "The Backoffice moves its teams and users to the surviving customer on its own when one row holds the licence or billing account; Customers → CRM Merges shows what happened, what a person has to decide, and the undo. Do not pick this row for a user or a team.",
  },
  [COMPANY_LINK_STATUS_NOT_LINKED]: {
    ...STATUS_DISPLAY[COMPANY_LINK_STATUS_NOT_LINKED],
    description:
      "No HubSpot company is linked to this customer, so no CRM data is shown here and the customer's identity is not pushed to HubSpot.",
    fix: "Link a HubSpot company — search by name or org.nr. A customer without a CRM record is also valid.",
  },
  [COMPANY_LINK_STATUS_PENDING_PUSH]: {
    ...STATUS_DISPLAY[COMPANY_LINK_STATUS_PENDING_PUSH],
    description:
      "The customer was edited after the last push to HubSpot — the company record still shows the previous identity values.",
    fix: "No action needed: the hourly sync pushes the app's values (app-wins). Editing and saving again pushes immediately.",
  },
}

export function getCompanyLinkStatusConfig(
  status: CompanyLinkStatusValue,
): CompanyLinkStatusConfig | undefined {
  return ACTIONABLE_CONFIG[status]
}

export function getCompanyLinkHealthyDisplay(): {
  variant: DotProps["variant"]
  label: string
} {
  return STATUS_DISPLAY[COMPANY_LINK_STATUS_IN_SYNC]
}
